import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../../utils/bookingApi';
import { formatPence } from '../../../utils/money';
import { formatDate } from '../../../utils/dates';
import { useAuth } from '../../../contexts/AuthContext';
import {
  PageHeader, FilterChips, Disclosure, Sheet, Picker, Skeleton, EmptyState, ErrorState, InlineError, useToast,
} from '../../../components/ui';
import CreditNoteForm from './CreditNoteForm';
import '../booking-shared.css';
import './AdminInvoices.css';

// Display status for an invoice, as a status label tone. Overdue is derived, never stored.
export function invoiceStatus(inv) {
  if (inv.status === 'SENT' && inv.isOverdue) return { key: 'OVERDUE', label: 'Overdue', tone: 'bad' };
  return {
    DRAFT: { key: 'DRAFT', label: 'Draft', tone: 'off' },
    SENT: { key: 'SENT', label: 'Sent', tone: 'wait' },
    PAID: { key: 'PAID', label: 'Paid', tone: 'good' },
    VOID: { key: 'VOID', label: 'Void', tone: 'off' },
  }[inv.status] || { key: inv.status, label: inv.status, tone: 'off' };
}

export const formatDay = (d) => (d ? formatDate(d) : '');

const CHIPS = [
  { id: '', label: 'All' }, { id: 'DRAFT', label: 'Drafts' }, { id: 'SENT', label: 'Sent' },
  { id: 'OVERDUE', label: 'Overdue' }, { id: 'PAID', label: 'Paid' }, { id: 'VOID', label: 'Void' },
];

function CreditNotes() {
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    bookingApi.getCreditNotes().then(res => setNotes(res.data)).catch(() => setError("Couldn't load credit notes."));
  }, []);
  useEffect(() => { load(); }, [load]);
  const download = (n) => {
    setError(null);
    bookingApi.downloadCreditNotePdf(n.id, `${n.creditNoteNumber}.pdf`).catch(() => setError(`Couldn't download ${n.creditNoteNumber}. Try again.`));
  };
  if (error && !notes) return <InlineError onRetry={load}>{error}</InlineError>;
  if (!notes) return <Skeleton rows={2} label="Loading credit notes…" />;
  return (
    <>
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      <div className="inv-list">
        {notes.length === 0 && <p className="inv-muted inv-empty">No credit notes yet.</p>}
        {notes.map(n => (
          <button key={n.id} type="button" className="inv-row" onClick={() => download(n)}>
            <span className="inv-row__main">
              <span className="inv-row__name">{n.creditNoteNumber}</span>
              <span className="inv-row__sub">
                {n.invoice
                  ? `${n.invoice.billToName} · against ${n.invoice.invoiceNumber} · ${formatDay(n.issueDate)}`
                  : `${n.businessContact?.name} · on account · ${formatDay(n.issueDate)}${n.available > 0 ? ` · ${formatPence(n.available)} left` : ''}`}
              </span>
            </span>
            <span className="inv-row__amt">{formatPence(n.total)}</span>
          </button>
        ))}
      </div>
    </>
  );
}

// Credit on account from the Invoices page: pick the business, then the usual form
function NewCreditNote({ onDone, onCancel }) {
  const [contacts, setContacts] = useState(null);
  const [contactId, setContactId] = useState('');
  const [error, setError] = useState(null);
  useEffect(() => {
    bookingApi.getBusinessContacts().then(res => setContacts(res.data)).catch(() => setError("Couldn't load your business contacts."));
  }, []);
  const items = useMemo(() => (contacts || []).map(c => ({
    id: c.id, label: c.name, detail: c.contactEmail || 'No email', search: c.contactEmail || '',
  })), [contacts]);
  const contact = (contacts || []).find(c => c.id === contactId);
  if (contact) return <CreditNoteForm key={contact.id} contact={contact} onDone={onDone} onCancel={onCancel} />;
  return (
    <Sheet
      title="New credit note"
      onClose={onCancel}
      footer={<div className="bk-actions"><button type="button" className="bk-btn" onClick={onCancel}>Cancel</button></div>}
    >
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      {!contacts && !error && <Skeleton rows={1} label="Loading contacts…" />}
      {contacts && contacts.length === 0 && (
        <p className="inv-muted inv-small" style={{ margin: 0 }}>
          Add the business first under <Link to="/booking/admin/business-contacts">Business contacts</Link>.
        </p>
      )}
      {contacts && contacts.length > 0 && (
        <div className="bk-label">
          <label htmlFor="new-credit-contact">Who is it for?</label>
          <Picker id="new-credit-contact" label="Who is it for?" items={items} value={contactId} onChange={setContactId}
            placeholder="Search business contacts…" noneText="No contact matches." />
        </div>
      )}
    </Sheet>
  );
}

export default function AdminInvoices() {
  const [status, setStatus] = useState('');
  const [invoices, setInvoices] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creditsVersion, setCreditsVersion] = useState(0);
  const toast = useToast();
  // Making invoices and credit notes needs Manage access to business
  const { can } = useAuth();
  const managesBusiness = can('business', 'MANAGE');

  const load = useCallback(() => {
    setLoadError(false);
    bookingApi.getInvoices(status || undefined)
      .then(res => setInvoices(res.data))
      .catch(() => setLoadError(true));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  let list;
  if (loadError) list = <ErrorState title="Couldn't load invoices" onRetry={load} />;
  else if (!invoices) list = <Skeleton rows={4} label="Loading invoices…" />;
  else if (invoices.length === 0) {
    list = (
      <EmptyState
        icon={DocumentTextIcon}
        title={status ? 'No invoices here' : 'No invoices yet'}
        text="Invoices are for other clubs and businesses, such as hall hire or kit."
        action={managesBusiness && !status ? <Link className="bk-btn bk-btn--primary" to="/booking/admin/invoices/new">New invoice</Link> : undefined}
      />
    );
  } else {
    list = (
      <div className="inv-list">
        {invoices.map(inv => {
          const s = invoiceStatus(inv);
          const who = inv.billToName || inv.businessContact?.name;
          const when = inv.status === 'DRAFT' ? 'Not sent' : inv.status === 'SENT' ? `Due ${formatDay(inv.dueDate)}` : formatDay(inv.issueDate);
          return (
            <Link key={inv.id} to={`/booking/admin/invoices/${inv.id}`} className="inv-row">
              <span className="inv-row__main">
                <span className="inv-row__name">{inv.invoiceNumber || 'Draft'}</span>
                <span className="inv-row__sub">{who} · {when}</span>
              </span>
              <span className={`bk-pill bk-pill--${s.tone}`}>{s.label}</span>
              <span className="inv-row__amt">{formatPence(inv.status === 'PAID' ? (inv.settled ?? inv.total - inv.credited) : inv.status === 'SENT' ? inv.amountDue : inv.total)}</span>
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--wide inv-page">
      <PageHeader
        back={{ to: '/admin-hub', label: 'Admin' }}
        title="Invoices"
        details="For other clubs and businesses."
        action={managesBusiness ? { label: 'New invoice', short: 'New', to: '/booking/admin/invoices/new' } : undefined}
      />
      <p className="inv-muted inv-small" style={{ margin: 0 }}>
        <Link className="bk-link" to="/booking/admin/business-contacts">Business contacts</Link>
      </p>

      <FilterChips label="Filter by status" chips={CHIPS} value={status} onChange={setStatus} />

      {list}

      <Disclosure summary="Credit notes" open={showCredits} onToggle={setShowCredits}>
        <CreditNotes key={creditsVersion} />
      </Disclosure>
      {managesBusiness && (
        <button type="button" className="bk-btn bk-btn--sm inv-self-start" onClick={() => setCreating(true)}>New credit note</button>
      )}

      {creating && (
        <NewCreditNote
          onCancel={() => setCreating(false)}
          onDone={(result) => {
            setCreating(false);
            setShowCredits(true);
            setCreditsVersion(v => v + 1);
            const number = result.creditNote.creditNoteNumber;
            toast(result.emailed ? `${number} issued and emailed` : `${number} issued`);
          }}
        />
      )}
    </div>
  );
}
